import { useEffect } from 'react';
import { Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown, MarkdownStorage } from 'tiptap-markdown';
import { cn } from '@/utils/lib/utils';

// prosemirror-markdown's serializer can emit a leading/trailing blank line for a
// single-paragraph doc; trim it so persisted values stay clean.
const getMarkdown = (editor: Editor) =>
  (editor.storage as unknown as { markdown: MarkdownStorage }).markdown.getMarkdown().trim();

interface MarkdownInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  dark?: boolean;
}

export function MarkdownInput({
  value = '',
  onChange,
  placeholder,
  className,
  disabled,
  dark = false,
}: Readonly<MarkdownInputProps>) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Markdown.configure({ html: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: updatedEditor }) => {
      onChange?.(getMarkdown(updatedEditor));
    },
  });

  useEffect(() => {
    if (!editor) return;
    const currentMarkdown = getMarkdown(editor);
    if (value !== currentMarkdown) {
      editor.commands.setContent(value);
    }
    // Only re-sync when the external value changes, not on every editor identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  const sharedClasses = cn(
    'w-full min-h-[40px] rounded-xl border px-3 py-2 text-sm transition-all break-words',
    '[&_.tiptap]:outline-none [&_.tiptap_p]:m-0',
    '[&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]',
    '[&_.tiptap_p.is-editor-empty:first-child::before]:text-gray-500',
    '[&_.tiptap_p.is-editor-empty:first-child::before]:float-left',
    '[&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none',
    '[&_.tiptap_p.is-editor-empty:first-child::before]:h-0',
    dark
      ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white'
      : 'border-gray-300 bg-white',
    disabled &&
      (dark
        ? 'cursor-not-allowed opacity-50 bg-gray-100 dark:bg-gray-800'
        : 'cursor-not-allowed opacity-50 bg-gray-100'),
    className,
  );

  return (
    <div className="relative">
      <div className={sharedClasses}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
